import React from 'react';
import '../grammar-page.css';
import { FaBookOpen, FaLightbulb } from 'react-icons/fa';

const ConditionalsExplanation = () => {
  return (
    <div className="grammar-explanation">
      <h2><FaBookOpen /> Conditional Sentences</h2>
      <p>
        Conditional sentences talk about hypothetical situations and their consequences.
        They have an <strong>if-clause</strong> (the condition) and a <strong>main clause</strong> (the result).
        There are four main types, plus mixed conditionals, each with a different degree of probability or time reference.
      </p>

      <div className="example-block">
        <h3><FaLightbulb /> Type 0: Zero Conditional (general truths)</h3>
        <ul>
          <li><strong>Structure:</strong> If + present simple, present simple</li>
          <li>Used for facts, scientific truths, and habits that are always true.</li>
          <li><em>If you heat water to 100°C, it boils.</em></li>
        </ul>
      </div>

      <div className="example-block">
        <h3><FaLightbulb /> Type 1: First Conditional (real possibilities)</h3>
        <ul>
          <li><strong>Structure:</strong> If + present simple, will + base verb</li>
          <li>Used for future situations that are real or likely.</li>
          <li><em>If it rains, I will stay at home.</em></li>
        </ul>
      </div>

      <div className="example-block">
        <h3><FaLightbulb /> Type 2: Second Conditional (unreal present/future)</h3>
        <ul>
          <li><strong>Structure:</strong> If + past simple, would + base verb</li>
          <li>Used for hypothetical or unlikely situations, or to give advice.</li>
          <li><em>If I won the lottery, I would travel the world.</em></li>
          <li><em>If I were you, I would take a coat.</em></li>
        </ul>
      </div>

      <div className="example-block">
        <h3><FaLightbulb /> Type 3: Third Conditional (unreal past)</h3>
        <ul>
          <li><strong>Structure:</strong> If + past perfect, would have + past participle</li>
          <li>Used for past situations that did not happen, often with regret or criticism.</li>
          <li><em>If I had studied harder, I would have passed the exam.</em></li>
        </ul>
      </div>

      <div className="example-block">
        <h3><FaLightbulb /> Mixed Conditionals</h3>
        <ul>
          <li><strong>Past condition → present result:</strong> If + past perfect, would + base verb</li>
          <li><em>If I had taken that job, I would be living in New York now.</em></li>
          <li><strong>Present condition → past result:</strong> If + past simple, would have + past participle</li>
          <li><em>If I weren't afraid of heights, I would have gone skydiving with them.</em></li>
        </ul>
      </div>

      <h3><FaLightbulb /> Summary</h3>
      <div className="grammar-flex-table">
        {[
          ['Type', 'Structure', 'Use'],
          ['0', 'If + present simple, present simple', 'General truths and facts'],
          ['1', 'If + present simple, will + base verb', 'Real, likely future'],
          ['2', 'If + past simple, would + base verb', 'Unreal or unlikely'],
          ['3', 'If + past perfect, would have + past participle', 'Unreal past'],
        ].map(([type, structure, use], index) => (
          <div key={index} className={`grammar-flex-row${index === 0 ? ' header' : ''}`}>
            <div className="grammar-cell">{type}</div>
            <div className="grammar-cell">{structure}</div>
            <div className="grammar-cell">{use}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ConditionalsExplanation;
